import React from "react";
import PropTypes from "prop-types";
import { useField, useFormikContext } from "formik";
import { Icon } from "@qgiv/core-react";
import { CardInput } from "@qgiv/core-donor";
import EarlyRegistrationChip from "./EarlyRegistrationChip";
import CommitmentDetailsChip from "./CommitmentDetailsChip";

const CategoryCard = ({
    categoryRef = {},
    commitmentAmount = "",
    description = "",
    details,
    earlyRegistration = false,
    hasCommitments = false,
    formattedDiscountedPrice = "",
    formattedPrice = "",
    hasDiscount,
    id,
    isCms = false,
    isFree = false,
    multipleCategories = false,
    name,
    title,
    type = "checkbox",
    handleCategoryChange,
    ...props
}) => {
    // -------------------------------------------------------------------------
    // NOTE: Make sure to cast value as a string, otherwise formik won't work
    // -------------------------------------------------------------------------
    const [field, , helpers] = useField({
        name,
        type,
        value: `${id}`,
        multiple: true,
    });
    const { setFieldValue, initialValues, values } = useFormikContext();

    const { Participant_Fundraising_Goal } = values;
    // reference to Participant_Fundraising_Goal initial value for categories without commitments
    const { Participant_Fundraising_Goal: initialParticipantFundraisingGoal } =
        initialValues;

    const _props = {
        id,
        title,
        name,
        type,
        handleBlur: (e) => {
            if (isCms) {
                return;
            }
            field.onBlur(e);
        },
        handleChange: (e) => {
            if (isCms) {
                return;
            }

            // If selected category has a commitment set Participant_Fundraising_Goal
            // so that the commitment amount pre populates the fundraising goal input
            if (Number(commitmentAmount) > 0 && hasCommitments) {
                setFieldValue(
                    "Participant_Fundraising_Goal",
                    String(commitmentAmount),
                );
            }

            // If category does not have commitments enabled, revert to the initial value
            if (
                !hasCommitments &&
                String(Participant_Fundraising_Goal) !==
                    String(initialParticipantFundraisingGoal)
            ) {
                setFieldValue(
                    "Participant_Fundraising_Goal",
                    String(initialParticipantFundraisingGoal),
                );
            }

            handleCategoryChange(e);
        },
        // Focus event is not being triggered on iOS/Safari so instead we have used a click event
        handleClick: () => {
            if (isCms) {
                return;
            }
            helpers.setTouched({ value: true, shouldValidate: false });
        },
        ...field,
        ...props,
    };

    if (field.checked) {
        // This prevents the edit icon from displaying if the is only one category
        if (multipleCategories) {
            _props.extraTitle = "Change category";
            // eslint-disable-next-line react/display-name
            _props.renderExtraTitle = () => (
                <div className="card__extra-title">
                    <span className="-type--large">
                        <Icon glyph="pencil-alt-regular" type="FontAwesome" />
                    </span>
                    <span className="-visually-hidden">{title}</span>
                </div>
            );
        }
    } else if (hasDiscount) {
        _props.extraTitle = `${formattedDiscountedPrice}`;
        const renderExtraTitle = (extraTitle) => (
            <div className="card__extra-title">
                <div>
                    <span className="-text-decoration--line-through -type--large">
                        {formattedPrice}
                    </span>{" "}
                    <span className="-type--large">{extraTitle}</span>
                </div>
            </div>
        );
        renderExtraTitle.displayName = "renderExtraTitle";
        _props.renderExtraTitle = renderExtraTitle;
    } else {
        _props.extraTitle = `${formattedPrice}`;
    }

    const commitmentDetailsChipStyle =
        !isFree && earlyRegistration ? "-margin-top--10" : "";

    return (
        <CardInput {..._props} ref={categoryRef}>
            {!field.checked && (
                <div className="category-card-display">
                    {description && description.length > 0 && description}
                    {((!isFree && earlyRegistration) || hasCommitments) && (
                        <div className="card__divider" />
                    )}
                    {!isFree && earlyRegistration && (
                        <EarlyRegistrationChip categoryId={id} />
                    )}
                    {/* this <br> required to display commitment chip
                        correctly in CMS as the container width differs than that
                        of the front facing event due to the summary section sidebar */}
                    {!isFree && earlyRegistration && hasCommitments && <br />}
                    {hasCommitments && (
                        <CommitmentDetailsChip
                            categoryId={id}
                            classNames={commitmentDetailsChipStyle}
                            location="category"
                            details={details}
                        />
                    )}
                </div>
            )}
        </CardInput>
    );
};

CategoryCard.propTypes = {
    categoryRef: PropTypes.shape({}),
    description: PropTypes.string,
    earlyRegistration: PropTypes.bool,
    formattedDiscountedPrice: PropTypes.string,
    formattedPrice: PropTypes.string,
    hasCommitments: PropTypes.bool,
    hasDiscount: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    isFree: PropTypes.bool,
    multipleCategories: PropTypes.bool,
    isCms: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.oneOf(["radio", "checkbox"]),
    commitmentAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    details: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string])
        .isRequired,
    handleCategoryChange: PropTypes.func.isRequired,
};

export default CategoryCard;
